import luhn from 'luhn';

/*
 Takes a block of input text and returns the same text with any potential credit card numbers redacted, except
 for the last four digits. Preceding digits are replaced with asterisks.

 The algorithm uses the following steps:
 1. Given input text, look for matches
 2. If matches found, identify the match region (e.g., with a capture from the regex)
 3. Normalize the matches (e.g., strip everything that isn't a digit)
 4. Luhn check
 5. If Luhn check passes on a match, redact
*/
export default function redactPaymentCard(record) {
  if (!record.length) {
    return record;
  }

  let matches = _findCardMatches(record);
  if (matches.length === 0) {
    return record;
  }

  let toRedact = [];
  matches.forEach(match => {
    // Strips non-digits out of matches for normalization
    let creditCard = record.substring(match[0], match[1]);
    let normalizedCC = creditCard.replace(/\D/g, '');

    if (luhn.validate(normalizedCC)) {
      toRedact.push({ matchingRange: match, normalizedMatchText: normalizedCC });
    }
  });

  if (toRedact.length === 0) {
    return record;
  }

  return _redactCardNumbersFromRecord(record, toRedact);
}

/*
  Given an input string, returns [start, end] indices (end is exclusive) of matching
  payment card patterns, if any exist. If there are no matches, the returned array is empty.
*/
function _findCardMatches(record) {
  /*
  This regex is the same that is used for the payment card redactor in Supernova;
  If the regex is updated here, it needs to be updated there as well.
  https://github.com/sagansystems/supernova/blob/master/domain/payment_card_redaction.go
  */
  const cards = {
    visaPattern: /4\d{3}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d(?:\d{3})?(?:(?:[\s-]*)?\d{3})?/,
    mastercardPattern: /5[1-5]\d{2}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}/,
    mastercardPattern2: /2(?:22[1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720)(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}/,
    amexPattern: /3[47]\d{2}(?:[\s-]*)?\d{6}(?:[\s-]*)?\d{5}/,
    // https://www.discovernetwork.com/downloads/IPP_VAR_Compliance.pdf
    dinersClubPattern: /3(?:0[0-5]|[68][0-9])\d{2}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{5}/,
    discoverPattern: /6(?:011|22[1-9]|4[4-9]\d|5\d{2})(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}/,
    jcbPattern: /35(?:2[89]|[3-8]\d)(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}/,
    maestroPattern: /(?:5[0678]\d{2}|6\d{3})(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{4}(?:[\s-]*)?\d{1,7}?/,
  };

  const cardRegex = new RegExp(
    `(?:(?:${cards.visaPattern.source})|(?:${cards.mastercardPattern.source})|(?:${cards.mastercardPattern2.source})|(?:${cards.amexPattern.source})|(?:${cards.discoverPattern.source})|(?:${cards.jcbPattern.source})|(?:${cards.maestroPattern.source})|(?:${cards.dinersClubPattern.source}))\\b`,
    `g`
  );

  let matches = [];
  let match = cardRegex.exec(record);
  while (match !== null) {
    matches.push([match.index, match.index + match[0].length]);
    match = cardRegex.exec(record);
  }

  return matches;
}

function _redactCardNumbersFromRecord(record, toRedact) {
  let redactedRecord = '';
  let inputStartIndex = 0;
  toRedact.forEach(redaction => {
    let displayDigitsStartIndex = redaction.normalizedMatchText.length - 4;
    redactedRecord += record.substring(inputStartIndex, redaction.matchingRange[0]);
    redactedRecord += `\xa0 • • • • \xa0 • • • • \xa0 • • • • \xa0 ${redaction.normalizedMatchText.substring(
      displayDigitsStartIndex
    )}`;
    inputStartIndex = redaction.matchingRange[1];
  });
  redactedRecord += record.substring(inputStartIndex);
  return redactedRecord;
}
